/**
 * EmployerDashboard - Dashboard para empregadores
 * 
 * Extraído do App.js durante refatoração Fase 1
 * 
 * Funcionalidades:
 * - Navegação por abas: Buscar Profissionais, Publicar Vaga, Minhas Vagas
 * - Tooltip com dicas
 * - Modal de exemplo
 * - Listagem de ofertas de profissionais
 * - Listagem de vagas publicadas
 * 
 * Dependências:
 * - useTranslation (traduções)
 * - ExampleModal (componente extraído)
 * - WorkOfferCard (componente extraído)
 * - JobPostForm (componente extraído)
 */

import React, { useState } from "react";
import axios from "axios";
import { useTranslation } from "../translations";
import { Button } from "./ui/button";
import { toast } from "sonner";
import ExampleModal from "./ExampleModal";
import WorkOfferCard from "./WorkOfferCard";
import JobPostForm from "./JobPostForm";
import { AvailableLaborCard } from "./ExternalJobsSection";
import { ShareJobDialog } from "./ShareJobDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const daysLeft = (expiresAt) => {
  if (!expiresAt) return null;
  const d = Math.ceil((new Date(expiresAt) - Date.now()) / 86400000);
  return Number.isNaN(d) ? null : Math.max(0, d);
};

const EmployerDashboard = ({ jobs, myJobs, workOffers, onUpdate }) => {
  const [activeEmployerTab, setActiveEmployerTab] = useState('browse');
  const [shareJob, setShareJob] = useState(null);
  const [showExample, setShowExample] = useState(false);
  const [showTip, setShowTip] = useState(true);
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      {/* Modal de Exemplo */}
      <ExampleModal isOpen={showExample} onClose={() => setShowExample(false)} type="vagas" />

      {/* Dica de Como Usar */}
      {showTip && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 relative">
          <button 
            onClick={() => setShowTip(false)}
            className="absolute top-2 right-2 text-blue-400 hover:text-blue-600"
          >
            ✕
          </button>
          <div className="flex items-start gap-3">
            <span className="text-2xl">💡</span>
            <div>
              <h4 className="font-semibold text-blue-800">{t('howToUse')}</h4>
              <p className="text-sm text-blue-700 mt-1">{t('tooltipJobPosting')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8 items-center">
          <button
            onClick={() => setActiveEmployerTab('browse')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeEmployerTab === 'browse'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200'
            }`}
          >
            {t('searchProfessionals')}
          </button>
          <button
            onClick={() => setActiveEmployerTab('post')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeEmployerTab === 'post'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200'
            }`}
          >
            {t('publishJobBtn')}
          </button>
          <button
            onClick={() => setActiveEmployerTab('my-jobs')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeEmployerTab === 'my-jobs'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200'
            }`}
          >
            {t('myJobsPublished')}
          </button>
          
          {/* Botão Ver Exemplo */}
          <button
            onClick={() => setShowExample(true)}
            className="ml-auto py-2 px-3 text-sm font-medium text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors flex items-center gap-1"
          >
            💡 {t('seeExample')}
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeEmployerTab === 'browse' && (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold">{t('searchProfessionals')}</h2>
          <p className="text-gray-600">{t('findQualifiedProfessionals')}</p>
          <div className="grid gap-4">
            {/* REAL WORK OFFERS WITH CHAT INTEGRATION */}
            {workOffers && workOffers.map((offer) => (
              <WorkOfferCard key={offer.id} offer={offer} isOwner={false} onUpdate={onUpdate} />
            ))}
            {(!workOffers || workOffers.length === 0) && (
              <p className="text-gray-500 text-center py-8">{t('noProfessionalsAvailable')}</p>
            )}
          </div>
          <AvailableLaborCard />
        </div>
      )}

      {activeEmployerTab === 'post' && (
        <JobPostForm onUpdate={onUpdate} />
      )}

      {activeEmployerTab === 'my-jobs' && (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold">{t('myPublishedJobs')}</h2>
          <div className="grid gap-4">
            {myJobs?.map((job) => {
              const dl = job.is_active === false ? null : daysLeft(job.expires_at);
              return (
              <div key={job.id} className="p-4 border rounded-lg">
                <h3 className="font-semibold">{job.title}</h3>
                <p className="text-gray-600">{job.description}</p>
                <p className="text-sm text-gray-500 mt-2">
                  📍 {job.location} | 💰 {job.salary_range}
                </p>
                {job.is_active === false ? (
                  <p className="text-xs text-gray-500 mt-1" data-testid={`job-closed-${job.id}`}>⛔ Vaga encerrada</p>
                ) : dl != null && (
                  <p className={`text-xs mt-1 ${dl <= 3 ? "text-amber-700 font-semibold" : "text-gray-500"}`} data-testid={`job-expiry-${job.id}`}>
                    ⏰ {dl > 0 ? `Sai do ar em ${dl} dia${dl !== 1 ? "s" : ""}` : "Expirada — renove para voltar ao ar"}
                  </p>
                )}
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    className="bg-sky-50 hover:bg-sky-100 text-sky-700 border-sky-200"
                    onClick={() => setShareJob(job)}
                    data-testid={`employer-share-job-btn-${job.id}`}
                  >
                    📢 Divulgar nos grupos
                  </Button>
                  {job.is_active !== false && dl != null && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/vaga/${job.id}/renew`);
                          toast.success("Vaga renovada por mais 15 dias! 🔄");
                          onUpdate && onUpdate();
                        } catch {
                          toast.error("Erro ao renovar a vaga");
                        }
                      }}
                      data-testid={`employer-renew-job-btn-${job.id}`}
                    >
                      🔄 Renovar por 15 dias
                    </Button>
                  )}
                </div>
              </div>
            );}) || []}
          </div>
          {(!myJobs || myJobs.length === 0) && (
            <p className="text-gray-500 text-center py-8">{t('noJobsPublished')}</p>
          )}
          <ShareJobDialog job={shareJob} open={!!shareJob} onOpenChange={(o) => !o && setShareJob(null)} />
        </div>
      )}
    </div>
  );
};

export default EmployerDashboard;
